import { useState, useEffect, useMemo } from 'react';
import { BREAKPOINTS, Breakpoint } from '../utils/style/constants.style';

type BreakpointState = {
  isXs: boolean;
  isSm: boolean;
  isMd: boolean;
  isLg: boolean;
  isXl: boolean;
  isXxl: boolean;
  current: Breakpoint;
  isAbove: (bp: Breakpoint) => boolean;
  isBelow: (bp: Breakpoint) => boolean;
};

const useBreakpoints = (): BreakpointState => {
  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    const mediaQueries = [
      window.matchMedia(`(max-width: ${BREAKPOINTS.xs}px)`),
      window.matchMedia(`(max-width: ${BREAKPOINTS.sm}px)`),
      window.matchMedia(`(max-width: ${BREAKPOINTS.md}px)`),
      window.matchMedia(`(max-width: ${BREAKPOINTS.lg}px)`),
      window.matchMedia(`(max-width: ${BREAKPOINTS.xl}px)`),
      window.matchMedia(`(max-width: ${BREAKPOINTS.xxl}px)`)
    ];

    const handleChange = () => {
      setWindowWidth(window.innerWidth);
    };

    mediaQueries.forEach((mq) => {
      mq.addEventListener('change', handleChange);
    });

    return () => {
      mediaQueries.forEach((mq) => {
        mq.removeEventListener('change', handleChange);
      });
    };
  }, []);

  const breakpointState: BreakpointState = useMemo(() => {
    const isXs = windowWidth <= BREAKPOINTS.xs;
    const isSm = windowWidth > BREAKPOINTS.xs && windowWidth <= BREAKPOINTS.sm;
    const isMd = windowWidth > BREAKPOINTS.sm && windowWidth <= BREAKPOINTS.md;
    const isLg = windowWidth > BREAKPOINTS.md && windowWidth <= BREAKPOINTS.lg;
    const isXl = windowWidth > BREAKPOINTS.lg && windowWidth <= BREAKPOINTS.xl;
    const isXxl = windowWidth > BREAKPOINTS.xl;

    let current: Breakpoint;
    if (isXs) {
      current = 'xs';
    } else if (isSm) {
      current = 'sm';
    } else if (isMd) {
      current = 'md';
    } else if (isLg) {
      current = 'lg';
    } else if (isXl) {
      current = 'xl';
    } else {
      current = 'xxl';
    }

    const isAbove = (bp: Breakpoint): boolean => {
      return windowWidth >= BREAKPOINTS[bp];
    };

    const isBelow = (bp: Breakpoint): boolean => {
      return windowWidth < BREAKPOINTS[bp];
    };

    return {
      isXs,
      isSm,
      isMd,
      isLg,
      isXl,
      isXxl,
      current,
      isAbove,
      isBelow
    };
  }, [windowWidth]);

  return breakpointState;
};

export default useBreakpoints;
